import React, { useState, useEffect, useRef } from 'react';
import tokenManager from '../utils/tokenManager';
import { Calendar, RefreshCw, Eye, CheckCircle, AlertTriangle, Info, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

const ICalSync = () => {
  const [status, setStatus] = useState({
    calendar_configured: false,
    ai_configured: false,
    ready: false
  });
  const [loading, setLoading] = useState(false);
  const [previews, setPreviews] = useState([]);
  const [syncResults, setSyncResults] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [icalUrls, setIcalUrls] = useState(['']);
  const [savedUrls, setSavedUrls] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [aiWorkLog, setAiWorkLog] = useState([]);
  const [showAiLog, setShowAiLog] = useState(false);
  const aiLogRef = useRef(null);

  useEffect(() => {
    checkCalendarStatus();
    loadSavedUrls();
  }, []);

  const checkCalendarStatus = async () => {
    try {
      const response = await fetch('/api/calendar/status', {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      } else {
        console.error('Failed to check calendar status');
      }
    } catch (error) {
      console.error('Error checking calendar status:', error);
    }
  };

  const loadSavedUrls = async () => {
    try {
      const response = await fetch('/api/calendar/get-urls', {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSavedUrls(data.ical_urls);
        // If there are saved URLs and current URLs are empty, load them
        if (data.ical_urls.length > 0 && icalUrls.length === 1 && icalUrls[0] === '') {
          setIcalUrls(data.ical_urls);
        }
      } else {
        console.error('Failed to load saved ICAL URLs');
      }
    } catch (error) {
      console.error('Error loading saved ICAL URLs:', error);
    }
  };

  const saveUrls = async () => {
    const validUrls = getValidUrls();
    if (validUrls.length === 0) {
      toast.error('Please add at least one ICAL URL');
      return;
    }

    try {
      setSaveLoading(true);
      const response = await fetch('/api/calendar/save-urls', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`
        },
        body: JSON.stringify({ ical_urls: validUrls })
      });

      if (response.ok) {
        const data = await response.json();
        setSavedUrls(data.urls);
        toast.success(`Saved ${data.saved_urls} ICAL URL(s) for automatic weekly sync!`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save ICAL URLs');
      }
    } catch (error) {
      toast.error('Error saving ICAL URLs');
      console.error('Error:', error);
    } finally {
      setSaveLoading(false);
    }
  };

  const addAiLogEntry = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setAiWorkLog(prev => [...prev, { timestamp, message, type }]);
    
    // Auto-scroll to bottom
    setTimeout(() => {
      if (aiLogRef.current) {
        aiLogRef.current.scrollTop = aiLogRef.current.scrollHeight;
      }
    }, 100);
  };

  const cleanupDuplicates = async () => {
    if (!window.confirm('This will use Claude AI to intelligently clean up duplicate events and fix event names without proper venues. Events with effects/risks will be preserved. Continue?')) {
      return;
    }

    try {
      setLoading(true);
      setAiWorkLog([]);
      setShowAiLog(true);
      
      addAiLogEntry('Starting AI cleanup analysis...', 'info');
      addAiLogEntry('Fetching Loveshack events from database...', 'info');
      
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute timeout
      
      const response = await fetch('/api/calendar/cleanup-duplicates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        
        // Display detailed AI work log from backend
        if (data.results.ai_log && data.results.ai_log.length > 0) {
          data.results.ai_log.forEach(logEntry => {
            const type = logEntry.includes('ERROR') ? 'error' : 
                        logEntry.includes('Deleting') ? 'warning' :
                        logEntry.includes('Found') || logEntry.includes('Analyzing') ? 'info' : 'success';
            addAiLogEntry(logEntry, type);
          });
        }
        
        addAiLogEntry(`Analysis complete. Processed ${data.results.analyzed} events.`, 'success');
        addAiLogEntry(`AI cleanup removed ${data.results.removed} events`, 'success');
        
        if (data.results.errors && data.results.errors.length > 0) {
          data.results.errors.forEach(error => {
            addAiLogEntry(`Warning: ${error}`, 'error');
          });
        }
        
        toast.success(`AI cleanup completed! Cleaned up ${data.results.removed} events from ${data.results.analyzed} analyzed`);
        
        // Show cleanup results
        if (data.results.duplicates_found && data.results.duplicates_found.length > 0) {
          console.log('Cleaned up duplicates:', data.results.duplicates_found);
        }
      } else {
        let errorMessage = 'Unknown error';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || 'Server returned error';
        } catch (parseError) {
          // If we can't parse JSON, it's probably an HTML error page
          const htmlError = await response.text();
          console.error('Server returned HTML instead of JSON:', htmlError);
          
          if (response.status === 408) {
            errorMessage = 'AI cleanup timed out - too many events to process';
          } else if (response.status === 500) {
            errorMessage = 'Server error during AI cleanup';
          } else {
            errorMessage = `Server error (${response.status}): Check console for details`;
          }
        }
        
        addAiLogEntry(`❌ Cleanup failed: ${errorMessage}`, 'error');
        toast.error(errorMessage);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        addAiLogEntry(`⏰ Request timed out after 2 minutes`, 'error');
        toast.error('AI cleanup timed out - process is taking too long');
      } else {
        addAiLogEntry(`❌ Network error: ${error.message}`, 'error');
        toast.error('Error during cleanup');
      }
      console.error('Error:', error);
    } finally {
      setLoading(false);
      addAiLogEntry('AI cleanup process finished', 'info');
    }
  };

  const addIcalUrl = () => {
    setIcalUrls([...icalUrls, '']);
  };

  const removeIcalUrl = (index) => {
    const newUrls = icalUrls.filter((_, i) => i !== index);
    setIcalUrls(newUrls);
  };

  const updateIcalUrl = (index, value) => {
    const newUrls = [...icalUrls];
    newUrls[index] = value;
    setIcalUrls(newUrls);
  };

  const getValidUrls = () => {
    return icalUrls.filter(url => url.trim() !== '');
  };

  const previewEvents = async () => {
    const validUrls = getValidUrls();
    if (validUrls.length === 0) {
      toast.error('Please add at least one ICAL URL');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/calendar/preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`
        },
        body: JSON.stringify({ ical_urls: validUrls })
      });

      if (response.ok) {
        const data = await response.json();
        setPreviews(data.previews);
        setShowPreview(true);
        toast.success(`Found ${data.total_events} events in ICAL feeds`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to preview events');
      }
    } catch (error) {
      toast.error('Error previewing events');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const syncEvents = async () => {
    const validUrls = getValidUrls();
    if (validUrls.length === 0) {
      toast.error('Please add at least one ICAL URL');
      return;
    }

    if (!window.confirm('This will import Loveshack gig events from your ICAL feeds. Continue?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/calendar/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`
        },
        body: JSON.stringify({ ical_urls: validUrls })
      });

      if (response.ok) {
        const data = await response.json();
        setSyncResults(data.results);
        
        // Display AI work log from sync process
        if (data.results.ai_log && data.results.ai_log.length > 0) {
          setShowAiLog(true);
          setAiWorkLog([]); // Clear previous logs
          data.results.ai_log.forEach(logEntry => {
            const type = logEntry.includes('❌') ? 'error' : 
                        logEntry.includes('✅') ? 'success' :
                        logEntry.includes('Warning:') ? 'warning' :
                        logEntry.includes('AI ') || logEntry.includes('Starting') ? 'info' : 'success';
            addAiLogEntry(logEntry, type);
          });
        }
        
        toast.success(`Successfully imported ${data.results.created} new Loveshack gigs!`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to sync events');
      }
    } catch (error) {
      toast.error('Error syncing events');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStatusIndicator = (configured, label) => {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        backgroundColor: configured ? '#dcfce7' : '#fef2f2',
        borderRadius: '6px',
        border: `1px solid ${configured ? '#bbf7d0' : '#fecaca'}`
      }}>
        {configured ? (
          <CheckCircle size={16} style={{ color: '#16a34a' }} />
        ) : (
          <AlertTriangle size={16} style={{ color: '#dc2626' }} />
        )}
        <span style={{
          fontSize: '14px',
          color: configured ? '#166534' : '#991b1b',
          fontWeight: '500'
        }}>
          {label}
        </span>
      </div>
    );
  };

  return (
    <div>
      <h3 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Calendar size={20} />
        ICAL Calendar Sync
      </h3>

      {/* Status Section */}
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <h4 style={{ marginBottom: '15px' }}>Configuration Status</h4>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '15px' }}>
          {renderStatusIndicator(status.calendar_configured, 'ICAL Feed Parser')}
          {renderStatusIndicator(status.ai_configured, 'AI Processing (Claude)')}
        </div>

        {status.ready && (
          <div style={{
            padding: '12px',
            backgroundColor: '#dcfce7',
            border: '1px solid #bbf7d0',
            borderRadius: '6px',
            color: '#166534'
          }}>
            <CheckCircle size={16} style={{ display: 'inline', marginRight: '8px' }} />
            <strong>Ready!</strong> System configured and ready to sync Loveshack gigs.
          </div>
        )}
      </div>

      {/* ICAL URL Input */}
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <h4 style={{ marginBottom: '15px' }}>ICAL Feed URLs</h4>
        
        {icalUrls.map((url, index) => (
          <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
            <input
              type="url"
              placeholder="https://calendar.google.com/calendar/ical/..."
              value={url}
              onChange={(e) => updateIcalUrl(index, e.target.value)}
              style={{
                flex: 1,
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
            {icalUrls.length > 1 && (
              <button
                onClick={() => removeIcalUrl(index)}
                style={{
                  padding: '8px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        ))}
        
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={addIcalUrl}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            <Plus size={16} />
            Add Another ICAL URL
          </button>
          
          <button
            onClick={saveUrls}
            disabled={saveLoading || getValidUrls().length === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              backgroundColor: saveLoading || getValidUrls().length === 0 ? '#9ca3af' : '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: saveLoading || getValidUrls().length === 0 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {saveLoading ? 'Saving...' : 'Save URLs for Weekly Sync'}
          </button>
        </div>
        
        {savedUrls.length > 0 && (
          <div style={{ 
            marginTop: '10px', 
            fontSize: '12px', 
            color: '#166534',
            backgroundColor: '#dcfce7',
            padding: '8px',
            borderRadius: '4px',
            border: '1px solid #bbf7d0'
          }}>
            ✅ <strong>Saved:</strong> {savedUrls.length} ICAL URL(s) configured for automatic weekly sync (Sundays at midnight)
          </div>
        )}
        
        <div style={{ 
          marginTop: '10px', 
          fontSize: '12px', 
          color: '#6b7280',
          backgroundColor: '#fff3cd',
          padding: '8px',
          borderRadius: '4px',
          border: '1px solid #ffeaa7'
        }}>
          <strong>Note:</strong> Only events with "From: Loveshack gigs" at the start of the description will be imported. Your private events will be ignored.
        </div>
      </div>

      {/* Actions Section */}
      {status.ready && (
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '20px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={previewEvents}
            disabled={loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1
            }}
          >
            <Eye size={16} />
            {loading ? 'Loading...' : 'Preview Loveshack Gigs'}
          </button>

          <button
            onClick={syncEvents}
            disabled={loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1
            }}
          >
            <RefreshCw size={16} />
            {loading ? 'Syncing...' : 'Sync Loveshack Gigs'}
          </button>

          <button
            onClick={cleanupDuplicates}
            disabled={loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              backgroundColor: '#f59e0b',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1
            }}
          >
            <Trash2 size={16} />
            {loading ? 'AI Cleaning...' : 'AI Cleanup Events'}
          </button>
        </div>
      )}

      {/* AI Work Log */}
      {showAiLog && aiWorkLog.length > 0 && (
        <div
          ref={aiLogRef}
          style={{
            backgroundColor: '#1a1a1a',
            color: '#e5e7eb',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontFamily: 'Monaco, "Lucida Console", "Courier New", monospace',
            fontSize: '12px',
            maxHeight: '300px',
            overflowY: 'auto',
            border: '1px solid #374151'
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '10px',
            paddingBottom: '8px',
            borderBottom: '1px solid #374151'
          }}>
            <h4 style={{ margin: 0, color: '#397a59', fontSize: '14px' }}>AI Work Log</h4>
            <button
              onClick={() => setShowAiLog(false)}
              style={{
                background: 'none',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ✕
            </button>
          </div>
          
          <div style={{ lineHeight: '1.4' }}>
            {aiWorkLog.map((entry, index) => (
              <div key={index} style={{
                marginBottom: '4px',
                color: entry.type === 'error' ? '#ef4444' : 
                       entry.type === 'success' ? '#10b981' : 
                       entry.type === 'warning' ? '#f59e0b' : '#e5e7eb'
              }}>
                <span style={{ color: '#6b7280' }}>[{entry.timestamp}]</span> {entry.message}
              </div>
            ))}
            {loading && (
              <div style={{ 
                marginTop: '8px', 
                color: '#10b981',
                animation: 'pulse 2s infinite'
              }}>
                <span style={{ color: '#6b7280' }}>[{new Date().toLocaleTimeString()}]</span> AI analysis in progress...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preview Results */}
      {showPreview && previews.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h4>Loveshack Gig Preview</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {previews.map((preview, index) => (
              <div key={index} style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '15px',
                backgroundColor: 'white'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div>
                    <h5 style={{ margin: '0 0 8px 0', color: '#374151' }}>Original ICAL Event</h5>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      <div><strong>Title:</strong> {preview.original.summary}</div>
                      <div><strong>Date:</strong> {preview.original.start}</div>
                      <div><strong>Location:</strong> {preview.original.location || 'Not specified'}</div>
                      {preview.original.description && (
                        <div><strong>Description:</strong> {preview.original.description}</div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <h5 style={{ margin: '0 0 8px 0', color: '#374151' }}>Claude AI Processed</h5>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      <div><strong>Event Name:</strong> {preview.processed.event_name}</div>
                      <div><strong>Venue:</strong> {preview.processed.venue_name}</div>
                      <div><strong>Type:</strong> {preview.processed.event_type}</div>
                      <div><strong>Outdoor:</strong> {preview.processed.is_outdoor ? 'Yes' : 'No'}</div>
                      {preview.processed.estimated_audience && (
                        <div><strong>Audience:</strong> {preview.processed.estimated_audience}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sync Results */}
      {syncResults && (
        <div style={{
          padding: '15px',
          backgroundColor: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#0369a1' }}>Sync Results</h4>
          <div style={{ fontSize: '14px', color: '#0c4a6e' }}>
            <div><strong>{syncResults.fetched}</strong> events fetched from ICAL feeds</div>
            <div><strong>{syncResults.processed}</strong> Loveshack gigs processed with AI</div>
            <div>✅ <strong>{syncResults.created}</strong> new gigs imported</div>
            <div>⏭️ <strong>{syncResults.skipped}</strong> gigs already existed</div>
            {syncResults.cleaned > 0 && (
              <div><strong>{syncResults.cleaned}</strong> duplicate events cleaned up by AI</div>
            )}
            {syncResults.errors.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <div style={{ color: '#dc2626' }}>❌ <strong>Errors:</strong></div>
                {syncResults.errors.map((error, index) => (
                  <div key={index} style={{ fontSize: '12px', color: '#dc2626', marginLeft: '20px' }}>
                    • {error}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ICalSync;
